import React, {useState, useEffect} from 'react'
import {
  Form,
  IconButton,
  Input,
  SelectPicker,
  Toggle,
  Grid,
  Row,
  Col,
  toaster,
  Message,
} from 'rsuite'

import {
  FullTenantFragment,
  useUpdateTenantMutation,
  useCreateTenantMutation,
  useTenantQuery,
  Tenant,
  TenantServicesDomainList,
  TenantServices,
  TenantQuery,
  TenantSslCertificate,
} from '../api'

import {useTranslation} from 'react-i18next'
import {ListInput, ListValue} from '../atoms/listInput'
import {EditorTemplate} from '../atoms/editorTemplate'
import {NavigationBar} from '../atoms/navigationBar'
import {IconButtonLink, TenantListRoute} from '../route'
import {CertEditPanel} from './certEditPanel'
import {slugifyString} from '../../shared/utility'

import ArrowLeft from '@rsuite/icons/legacy/ArrowLeft'
import Save from '@rsuite/icons/legacy/Save'
import {OverlayLoader} from '../atoms/loader'
import {generateID} from '../utility'
import {ClientSettings} from '../../shared/types'
import {ElementID} from '../../shared/elementID'

export interface TenantEditPanelProps {
  id?: string
  response?: {status: number; message: string} | null
  isApplyLoading?: boolean

  onSave(tenant: FullTenantFragment, oldData: TenantQuery | undefined): void
}

export interface TenantSslCertificateFe extends TenantSslCertificate {
  publicKey?: string
  privateKey?: string
}

export function TenantEditPanel({id, response, onSave, isApplyLoading}: TenantEditPanelProps) {
  const {t} = useTranslation()

  const [name, setName] = useState('')
  const [active, setActive] = useState(true)
  const [forceSingleGroup, setForceSingleGroup] = useState(true)

  // Basic auth for the whole website (all frontend domains). The password is
  // write-only: it is sent (and hashed) once on save and never read back.
  const [basicAuthActive, setBasicAuthActive] = useState(false)
  const [basicAuthUser, setBasicAuthUser] = useState('')
  const [basicAuthPass, setBasicAuthPass] = useState('')
  const [basicAuthHasPassword, setBasicAuthHasPassword] = useState(false)

  const [apiDomains, setApiDomains] = useState<ListValue<TenantServicesDomainList>[]>([])
  const [editorDomains, setEditorDomains] = useState<ListValue<TenantServicesDomainList>[]>([])
  const [frontendDomains, setFrontendDomains] = useState<ListValue<TenantServicesDomainList>[]>([])
  const [mediaDomains, setMediaDomains] = useState<ListValue<TenantServicesDomainList>[]>([])
  const [mediaProxyIndex, setMediaProxyIndex] = useState(
    `website-media-index-${slugifyString(generateID())}`
  )
  const [mediaProxyNoIndex, setMediaProxyNoIndex] = useState(
    `website-media-no-index-${slugifyString(generateID())}`
  )

  const [selectCerts, setSelectCerts] = useState<ListValue<any>[]>([])
  const [certs, setCerts] = useState<ListValue<TenantSslCertificateFe>[]>([])

  const pageTitle = id ? t('tenantList.panels.editTenant') : t('tenantList.panels.createTenant')

  const protocols = [
    {
      id: '1',
      label: 'https://',
      value: 'https://',
    },
    {
      id: '2',
      label: 'http://',
      value: 'http://',
    },
  ]

  const {
    data,
    loading: isLoading,
    error: loadError,
  } = useTenantQuery({
    variables: {id: id!},
    fetchPolicy: 'network-only',
    skip: id === undefined,
  })

  const [createTenant, {loading: isCreating, error: createError}] = useCreateTenantMutation()
  const [updateTenant, {loading: isUpdating, error: updateError}] = useUpdateTenantMutation()

  const isDisabled =
    isApplyLoading || isLoading || isCreating || isUpdating || loadError !== undefined

  async function getDomainList(list: any[]): Promise<ListValue<TenantServicesDomainList>[]> {
    return list.map((item) => {
      return {
        id: item.id,
        value: item,
      }
    })
  }

  async function getCertList(list: any[]): Promise<ListValue<TenantSslCertificate>[]> {
    return list.map((item) => {
      return {
        id: item.id,
        value: item,
      }
    })
  }

  const {showTenantSslCert}: ClientSettings = JSON.parse(
    document.getElementById(ElementID.Settings)!.textContent!
  )

  useEffect(() => {
    setName(data?.tenant?.name ?? '')
    setActive(data?.tenant?.active ?? true)
    setForceSingleGroup(data?.tenant?.forceSingleGroup ?? false)
    setBasicAuthActive(data?.tenant?.basicAuth?.active ?? false)
    setBasicAuthUser(data?.tenant?.basicAuth?.user ?? '')
    setBasicAuthPass('')
    setBasicAuthHasPassword(data?.tenant?.basicAuth?.hasPassword ?? false)

    const tenant = data?.tenant?.services as TenantServices

    const fetchData = async () => {
      if (tenant && tenant.editor && tenant.editor.domainList) {
        setEditorDomains(await getDomainList(tenant.editor.domainList))
      }

      if (tenant && tenant.api && tenant.api.domainList) {
        setApiDomains(await getDomainList(tenant.api.domainList))
      }

      if (tenant && tenant.frontends && tenant.frontends.domainList) {
        setFrontendDomains(await getDomainList(tenant.frontends.domainList))
      }

      if (tenant && tenant.media && tenant.media.domainList) {
        setMediaDomains(await getDomainList(tenant.media.domainList))
      }

      if (tenant && tenant.certificates) {
        setCerts(await getCertList(tenant.certificates))
      }

      if (tenant && tenant.mediaProxy.index) {
        setMediaProxyIndex(tenant.mediaProxy.index)
      }

      if (tenant && tenant.mediaProxy.noIndex) {
        setMediaProxyNoIndex(tenant.mediaProxy.noIndex)
      }
    }

    fetchData().catch(console.error)
  }, [data, data?.tenant])

  useEffect(() => {
    const selectCerts = certs
      .filter((item) => {
        if (item.value.name && item.id) {
          return item
        }
        return null
      })
      .map((item) => {
        const label = item.value.name.split('-wpid-')[0]

        return {
          id: item.id,
          value: slugifyString(item.value.name),
          label: label,
        }
      })

    selectCerts.unshift({id: '1', value: 'googlemanaged', label: `Google managed`})

    if (selectCerts) setSelectCerts(selectCerts)
  }, [certs])

  useEffect(() => {
    const error = loadError?.message ?? createError?.message ?? updateError?.message
    if (error) {
      toaster.push(
        <Message closable type="error">
          {error}
        </Message>,
        {duration: 0}
      )
    }
  }, [loadError, createError, updateError])

  useEffect(() => {
    if (response) {
      const {status, message} = response

      if (status !== 200) {
        toaster.push(
          <Message closable type="error">
            {t('global.error.apply')}
            <br />
            {message}
          </Message>,
          {duration: 0}
        )
      } else {
        toaster.push(<Message type="success">{t('global.success.apply')}</Message>, {
          duration: 2000,
        })
      }
    }
  }, [response, t])

  useEffect(() => {
    document.title = pageTitle
  })

  async function handleSave() {
    if (!name) {
      toaster.push(
        <Message closable type="error">
          {t('tenantList.panels.nameNotSet')}
        </Message>,
        {duration: 3000}
      )
      return
    }

    const dbInput = {
      name: '',
      services: {
        api: {
          domainList: apiDomains?.map((item) => {
            return {
              id: item.id,
              ingressName: `api-${slugifyString(item.id)}`,
              domain: item.value.domain,
              certificate: item.value.certificate,
              protocol: item.value.protocol,
            }
          }),
        },
        editor: {
          domainList: editorDomains?.map((item) => {
            return {
              id: item.id,
              ingressName: `editor-${slugifyString(item.id)}`,
              domain: item.value?.domain,
              certificate: item.value?.certificate,
              protocol: item.value?.protocol,
            }
          }),
        },
        frontends: {
          domainList: frontendDomains?.map((item) => {
            return {
              id: item.id,
              ingressName: `frontend-${slugifyString(item.id)}`,
              domain: item.value?.domain,
              certificate: item.value?.certificate,
              protocol: item.value?.protocol,
            }
          }),
        },
        media: {
          domainList: mediaDomains?.map((item) => {
            return {
              id: item.id,
              ingressName: `media-${slugifyString(item.id)}`,
              domain: item.value.domain,
              certificate: item.value.certificate,
              protocol: item.value.protocol,
            }
          }),
        },
        certificates: certs.map((item) => {
          return {
            id: item.id,
            name: slugifyString(item.value.name),
          }
        }),
        mediaProxy: {
          index: mediaProxyIndex,
          noIndex: mediaProxyNoIndex,
        },

        service: '',
      },
      // One gate for the whole website. The password is sent only when the field
      // was filled in: the API hashes it and keeps the previous hash when it is
      // absent, so leaving the field empty means "unchanged".
      basicAuth: {
        active: basicAuthActive,
        user: basicAuthUser,
        ...(basicAuthPass ? {pass: basicAuthPass} : {}),
      },
      active: active,
      forceSingleGroup: forceSingleGroup,
    } as Tenant

    const kubernetesConfigInput = JSON.parse(JSON.stringify(dbInput))

    kubernetesConfigInput.services.certificates = certs.map((item) => {
      return {
        id: item.id,
        name: slugifyString(item.value.name),
        privateKey: item.value.privateKey,
        publicKey: item.value.publicKey,
      }
    })

    // A refused save, such as basic auth without a password, rejects the mutation. The
    // effect on createError and updateError shows the API's message; the rejection is
    // caught so it does not surface as an uncaught error as well.
    const refused = () => ({data: undefined})

    if (id && data?.tenant) {
      kubernetesConfigInput.name = data.tenant.name

      dbInput.name = data.tenant.name

      const {data: updateData} = await updateTenant({
        variables: {
          id: id,
          input: dbInput,
        },
      }).catch(refused)

      if (updateData?.updateTenant) onSave({id: id, ...kubernetesConfigInput}, data)
    } else {
      kubernetesConfigInput.name = name

      dbInput.name = name
      const {data: createData} = await createTenant({
        variables: {
          input: dbInput,
        },
      }).catch(refused)

      if (createData?.createTenant)
        onSave({id: createData.createTenant.id, ...kubernetesConfigInput}, data)
    }
  }

  if (isLoading) return <>loading...</>

  function domainItem(value: any, onChange: any) {
    return (
      <Grid fluid>
        <Row className="show-grid">
          <Col xsHidden xs={5}>
            <SelectPicker
              data={protocols}
              defaultValue={value.protocol}
              onChange={(protocol) => {
                onChange({...value, protocol})
              }}
              searchable={false}
              disabled={isDisabled}
              block
            />
          </Col>
          <Col xsHidden xs={19}>
            <Input
              placeholder={t('tenantList.panels.domain')}
              value={value.domain}
              onChange={(domain) => {
                onChange({...value, domain})
              }}
            />
          </Col>
        </Row>
      </Grid>
    )
  }

  return (
    <EditorTemplate
      navigationChildren={
        <NavigationBar
          leftChildren={
            <IconButtonLink
              size={'lg'}
              icon={<ArrowLeft />}
              route={TenantListRoute.create({})}
              disabled={isDisabled}
              // onClick={(e) => {
              //   // if (!unsavedChangesDialog()) e.preventDefault()
              // }}
            >
              {t('global.buttons.cancel')}
            </IconButtonLink>
          }
          rightChildren={
            <IconButton
              style={{
                marginLeft: '10px',
              }}
              size={'lg'}
              icon={<Save />}
              disabled={isDisabled}
              onClick={() => {
                handleSave()
              }}
            >
              {t('global.buttons.save')}
            </IconButton>
          }
        />
      }
    >
      <div
        style={{
          width: '100%',
        }}
      >
        {isApplyLoading && <OverlayLoader />}
        <h4>{pageTitle}</h4>
        <br />
        <Form fluid>
          <Form.Group>
            <Form.ControlLabel>{t('tenantList.panels.name')}</Form.ControlLabel>
            <Form.Control
              name={t('tenantList.panels.name')}
              value={name}
              disabled={isDisabled || !!id}
              onChange={(value) => setName(slugifyString(value, false))}
            />
          </Form.Group>

          <Form.Group>
            <Form.ControlLabel>{t('tenantList.panels.active')}</Form.ControlLabel>
            <Toggle
              checked={active}
              disabled={isDisabled}
              onChange={(value) => {
                setActive(value)
              }}
            />
          </Form.Group>

          <Form.Group>
            <Form.ControlLabel>{t('tenantList.panels.forceSingleGroup')}</Form.ControlLabel>
            <Toggle
              checked={forceSingleGroup}
              disabled={isDisabled}
              onChange={(value) => {
                setForceSingleGroup(value)
              }}
            />
          </Form.Group>

          <Form.Group>
            <Form.ControlLabel>{t('tenantList.panels.basicAuth')}</Form.ControlLabel>
            <Toggle
              checked={basicAuthActive}
              disabled={isDisabled}
              onChange={(value) => {
                setBasicAuthActive(value)
              }}
            />
            {basicAuthActive && (
              <div style={{display: 'flex', marginTop: '10px'}}>
                <Input
                  placeholder={t('tenantList.panels.user')}
                  value={basicAuthUser}
                  disabled={isDisabled}
                  onChange={(value) => setBasicAuthUser(value)}
                />
                <Input
                  // The stored password is never read back, so an empty field means
                  // "keep the current one" — say so rather than showing a blank that
                  // reads as "no password set".
                  placeholder={
                    basicAuthHasPassword
                      ? t('tenantList.panels.passwordUnchanged')
                      : t('tenantList.panels.password')
                  }
                  value={basicAuthPass}
                  type="password"
                  disabled={isDisabled}
                  onChange={(value) => setBasicAuthPass(value)}
                />
              </div>
            )}
          </Form.Group>

          {/* {showTenantSslCert && (
            <>
              <h6 className="wep-section-title">{t('tenantList.panels.sslCerts')}</h6>
              <Form.Group>
                <ListInput
                  value={certs}
                  onChange={(certs) => {
                    setCerts(certs)
                  }}
                  disabled={isDisabled}
                  defaultValue={{
                    id: '',
                    name: '',
                    publicKey: '',
                    privateKey: '',
                  }}
                >
                  {(props) => <CertEditPanel {...props} />}
                </ListInput>
              </Form.Group>
              <br />
            </>
          )} */}

          <h6 className="wep-section-title">{t('tenantList.panels.apiDomain')}</h6>
          <ListInput
            maxItems={1}
            value={apiDomains}
            onChange={(domains) => setApiDomains(domains)}
            disabled={isDisabled}
            defaultValue={{
              protocol: 'https://',
              domain: '',
              certificate: 'googlemanaged',
              id: '',
              ingressName: '',
            }}
          >
            {({value, onChange}) => domainItem(value, onChange)}
          </ListInput>
          <br />

          <h6 className="wep-section-title">{t('tenantList.panels.editorDomain')}</h6>
          <ListInput
            maxItems={1}
            value={editorDomains}
            onChange={(domains) => setEditorDomains(domains)}
            disabled={isDisabled}
            defaultValue={{
              protocol: 'https://',
              domain: '',
              certificate: 'googlemanaged',
              id: '',
              ingressName: '',
            }}
          >
            {({value, onChange}) => domainItem(value, onChange)}
          </ListInput>
          <br />

          <h6 className="wep-section-title">{t('tenantList.panels.domainListFrontend')}</h6>
          <ListInput
            value={frontendDomains}
            onChange={(frontendDomain) => setFrontendDomains(frontendDomain)}
            disabled={isDisabled}
            defaultValue={{
              id: '',
              ingressName: '',
              protocol: 'https://',
              domain: '',
              certificate: 'googlemanaged',
            }}
          >
            {({value, onChange}) => domainItem(value, onChange)}
          </ListInput>
          <br />

          <h6 className="wep-section-title">{t('tenantList.panels.mediaDomainList')}</h6>
          <ListInput
            maxItems={1}
            value={mediaDomains}
            onChange={(domains) => setMediaDomains(domains)}
            disabled={isDisabled}
            defaultValue={{
              protocol: 'https://',
              domain: '',
              certificate: 'googlemanaged',
              id: '',
              ingressName: '',
            }}
          >
            {({value, onChange}) => domainItem(value, onChange)}
          </ListInput>
          <br />
        </Form>
      </div>
    </EditorTemplate>
  )
}
